import { APIConfig } from "@/lib/api";
import { ApiError } from "@/lib/api/api-error";
import { request as __request } from "@/lib/api/request";
import { getCsrfToken } from "@/providers/auth";
import { z } from "zod";
import {
  type Order,
  type Paginated,
  dashboardDataSchema,
  orderSchema,
  paginatedOrdersSchema,
} from "./types/orders";
import type { OrderInfo } from "./types/uploads";

const createOrderResponseSchema = z.object({
  order_id: z.union([z.string(), z.number()]).optional(),
  supplier_order_number: z
    .union([z.string(), z.number()])
    .nullable()
    .optional(),
});

const pushOrderToERPResponseSchema = z.object({
  detail: z.string(),
  order_id: z.number(),
  erp_type: z.string(),
  erp_order_id: z.string().nullable().optional(),
});

export type CreateOrderResponse = z.infer<typeof createOrderResponseSchema>;
export type PushOrderToERPResponse = z.infer<
  typeof pushOrderToERPResponseSchema
>;
export type OrderPatch = Partial<
  Pick<
    Order,
    | "status"
    | "expected_delivery_date"
    | "restaurant_address"
    | "matched_customer_contact_id"
    | "matched_customer_contact_name"
    | "matched_customer_approved"
  >
>;

const toLineItemsPayload = (order: Order) =>
  order.order_details.map((item) => ({
    ...(item.line_item_id != null ? { id: item.line_item_id } : {}),
    original_item_name: item.name,
    quantity: item.quantity,
    entered_uom: item.entered_uom ?? null,
    product: item.product_id ?? null,
    product_sku: item.sku ?? "",
    manual_override: item.manual_override,
  }));

const toOrderDetailsPayload = (order: Order) =>
  order.order_details.map(
    ({
      unit: _unit,
      entered_uom: _enteredUom,
      normalized_uom: _normalizedUom,
      ...item
    }) => item,
  );

export class OrderService {
  /** Fetches ALL orders by paging until no `next` remains */
  static async getOrders({
    pageSize = 50,
  }: { pageSize?: number } = {}): Promise<Order[]> {
    const all: Order[] = [];
    let pageUrl: string | null = "/api/v1/orders/";
    let isFirstPage = true;

    try {
      while (pageUrl) {
        const data = (await __request(
          APIConfig,
          {
            method: "GET",
            url: pageUrl,
            query: isFirstPage ? { page: 1, page_size: pageSize } : undefined,
          },
          paginatedOrdersSchema, // ✅ 3rd arg required
        )) as Paginated<Order>;

        all.push(...data.results);

        // If `next` is a full URL, extract just the path for `__request`
        if (data.next) {
          try {
            const u = new URL(data.next);
            pageUrl = u.pathname + u.search; // keep path+query
          } catch {
            // relative URL
            pageUrl = data.next;
          }
          isFirstPage = false;
        } else {
          pageUrl = null;
        }
      }
    } catch (error) {
      console.error("[getOrders] Unexpected error:", error);
    }

    return all;
  }

  /** Fetches a single page of orders */
  static async getOrdersPage({
    page = 1,
    pageSize = 50,
  }: {
    page?: number;
    pageSize?: number;
  } = {}): Promise<Paginated<Order>> {
    try {
      const data = (await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/orders/",
          query: { page, page_size: pageSize },
        },
        paginatedOrdersSchema,
      )) as Paginated<Order>;
      return data;
    } catch (error) {
      console.error("[getOrdersPage] Unexpected error:", error);
      return { count: 0, next: null, previous: null, results: [] };
    }
  }

  static async getOrderById(id: string) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/orders/{id}/",
          path: {
            id,
          },
        },
        orderSchema,
      );
      return data as Order;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getOrderById] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getOrderById] Unexpected error:", error);
      }
      return null;
    }
  }

  static async updateOrderPut(id: number, order: Order) {
    try {
      const data = JSON.parse(JSON.stringify(order));
      data.order_details = JSON.stringify(toOrderDetailsPayload(order));
      data.line_items = toLineItemsPayload(order);

      return await __request(
        APIConfig,
        {
          method: "PUT",
          url: "/api/v1/orders/{id}/",
          path: {
            id: id.toString(),
          },
          headers: {
            "Content-Type": "application/json",
          },
          body: data,
        },
        orderSchema,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[updateOrderPut] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[updateOrderPut] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async updateOrderStatus(id: number, status: Order["status"]) {
    return OrderService.updateOrderPatch(id, { status });
  }

  static async updateOrderPatch(id: number, body: OrderPatch) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "PATCH",
          url: "/api/v1/orders/{id}/",
          path: {
            id: id.toString(),
          },
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
          body,
        },
        orderSchema,
      );
      return data as Order;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[updateOrderStatus] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[updateOrderStatus] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async pushOrderToERP(
    orderId: number,
  ): Promise<PushOrderToERPResponse> {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "POST",
          url: "/api/v1/orders/{id}/push-to-erp/",
          path: { id: orderId.toString() },
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            Accept: "application/json",
          },
        },
        pushOrderToERPResponseSchema,
      );

      return data as PushOrderToERPResponse;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[OrderService.pushOrderToERP] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[OrderService.pushOrderToERP] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async waitForERPOrderId(
    orderId: number,
    {
      attempts = 12,
      intervalMs = 1500,
    }: {
      attempts?: number;
      intervalMs?: number;
    } = {},
  ): Promise<Order | null> {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const order = await OrderService.getOrderById(orderId.toString());

      if (order?.erp_order_id) {
        return order;
      }

      if (attempt < attempts - 1) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, intervalMs);
        });
      }
    }

    return null;
  }

  static async pushOrderToERPAndWaitForERPId(
    orderId: number,
  ): Promise<string | null> {
    const response = await OrderService.pushOrderToERP(orderId);

    if (response.erp_order_id) {
      return response.erp_order_id;
    }

    const order = await OrderService.waitForERPOrderId(orderId);
    return order?.erp_order_id ?? null;
  }

  /** Page until we’ve collected all orders since `startISO` (inclusive). */
  static async getOrdersSince(
    startISO: string,
    pageSize = 100,
  ): Promise<Order[]> {
    const startMs = new Date(startISO).getTime();
    const all: Order[] = [];
    let page = 1;

    for (;;) {
      const resp = (await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/orders/",
          query: { page, page_size: pageSize },
        },
        paginatedOrdersSchema,
      )) as Paginated<Order>;

      // Keep only those within range from this page
      const inRange = resp.results.filter(
        (o) => new Date(o.order_datetime).getTime() >= startMs,
      );
      all.push(...inRange);

      // If this page is already older than the start, we can stop
      const last = resp.results.at(-1);
      const hitOlder =
        !last || new Date(last.order_datetime).getTime() < startMs;

      if (hitOlder || !resp.next) {
        break;
      }
      page += 1;
    }

    return all;
  }

  static async getDashboardData() {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/orders/dashboard-data/",
        },
        dashboardDataSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getDashboardData] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getDashboardData] Unexpected error:", error);
      }
      return null;
    }
  }

  static async create(
    requestId: string,
    fileId: string,
    order_info: OrderInfo | null,
  ): Promise<CreateOrderResponse | null> {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "POST",
          url: "/api/v1/orders/",
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
          body: {
            requestId: requestId,
            fileId: fileId,
            order_info: order_info,
          },
        },
        createOrderResponseSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[OrderService.create] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[OrderService.create] Unexpected error:", error);
      }
      return null;
    }
  }
}
